import React, {useState, useEffect, useCallback, useRef} from 'react';
import {DialogActions, TextField, Button, Slide} from '@mui/material';
import {TransitionProps} from '@mui/material/transitions';
import Dialog from './Dialog';

const Transition = React.forwardRef(function Transition(
   props: TransitionProps & {children: React.ReactElement},
   ref: React.Ref<unknown>
) {
   return <Slide direction="up" ref={ref} {...props} />;
});

type Props = {
   title: string;
   open: boolean;
   initialValue: string;
   validate?: (value: string) => string;
   onClose: () => void;
   onSave: (value: string) => void;
};

const RenameDialog: React.FC<Props> = ({
   title = 'Rename',
   open,
   initialValue,
   validate,
   onClose,
   onSave,
}) => {
   const [value, setValue] = useState(initialValue);
   const [error, setError] = useState('');

   useEffect(() => {
      if (open) {
         setValue(initialValue);
         setError('');
      }
   }, [open, initialValue]);

   const inputRef = useRef<HTMLInputElement>(null);

   useEffect(() => {
      if (open) {
         const timer = setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
         }, 100); // ждём пока transition закончит mount

         return () => clearTimeout(timer);
      }
   }, [open]);

   const handleSave = useCallback(() => {
      const trimmed = value.trim();
      const validationError = validate ? validate(trimmed) : '';

      if (validationError) {
         setError(validationError);
         return;
      }

      onSave(trimmed);
   }, [value, validate, onSave]);

   return (
      <Dialog
         open={open}
         close={onClose}
         fullWidth
         maxWidth="xs"
         header={title}
         TransitionComponent={Transition}
      >
         <TextField
            inputRef={inputRef}
            autoFocus
            fullWidth
            margin="dense"
            label="New name"
            value={value}
            error={Boolean(error)}
            helperText={error}
            inputProps={{enterKeyHint: 'done'}}
            onChange={(e) => {
               setValue(e.target.value);
               if (error) setError('');
            }}
            onKeyDown={(e) => {
               if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
               }
            }}
         />

         <DialogActions sx={{px: 3, pb: 2}}>
            <Button onClick={onClose} color="inherit">
               Cancel
            </Button>
            <Button
               variant="contained"
               color="primary"
               onClick={handleSave}
               disabled={!value.trim()}
            >
               Save
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default React.memo(RenameDialog);
